/*
 * <oilogic-carousel> — horizontal carousel powered by Embla Carousel with
 * prev/next controls.
 *
 * Progressive enhancement: the track renders as a native overflow scroller
 * (see CSS) so it stays usable with touch/trackpad even before — or without —
 * Embla. When the Embla global is available this element upgrades the viewport
 * to a dragabble, snapping Embla carousel and wires the arrow buttons +
 * disabled-state handling. Re-inits on Theme Editor reload via connectedCallback.
 *
 * Expected markup (hooks are data attributes, styling is the consumer's):
 *   <oilogic-carousel>
 *     <div data-carousel-viewport>        <!-- Embla viewport -->
 *       <ul data-carousel-track> … </ul>  <!-- Embla container -->
 *     </div>
 *     <button data-carousel-prev>…</button>
 *     <button data-carousel-next>…</button>
 *   </oilogic-carousel>
 *
 * Embla itself is loaded on demand from the element's data-embla-url via the
 * shared singleton loader (embla-loader.js -> window.oilogicEmbla), so the
 * ~18KB library is fetched at most once per page and never as a render-blocking
 * static tag.
 */
// Several sections load this script (including the global cart drawer), so a
// page with more than one carousel would execute it twice and redeclare the
// class. Wrap in an IIFE (scopes the class) + guard so it defines at most once
// per page — same pattern as embla-loader.js and component-marquee.js.
(() => {
  if (customElements.get('oilogic-carousel')) return;

  class OilogicCarousel extends HTMLElement {
    #ctrl = new AbortController();
    #embla = null;

    connectedCallback() {
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');
      if (!this.viewport) return;

      // Upgrade to Embla when it is available; on failure the element stays a
      // usable native overflow scroller (progressive enhancement).
      if (typeof window.EmblaCarousel === 'function') {
        this.#init();
      } else if (typeof window.oilogicEmbla === 'function') {
        window.oilogicEmbla(this.dataset.emblaUrl)
          .then(() => this.#init())
          .catch(() => {});
      }
    }

    disconnectedCallback() {
      this.#ctrl.abort();
      this.#embla?.destroy();
      this.#embla = null;
    }

    #init() {
      if (this.#embla || typeof window.EmblaCarousel !== 'function') return;

      const { signal } = this.#ctrl;
      this.#embla = window.EmblaCarousel(this.viewport, {
        align: 'start',
        containScroll: 'trimSnaps',
        dragFree: false,
        loop: false,
        duration: 22,
      });

      this.prevBtn?.addEventListener('click', () => this.#embla.scrollPrev(), { signal });
      this.nextBtn?.addEventListener('click', () => this.#embla.scrollNext(), { signal });

      this.#embla.on('select', () => this.#update());
      this.#embla.on('reInit', () => this.#update());

      this.dataset.ready = '';
      this.#update();
    }

    #update() {
      if (!this.#embla) return;
      const canPrev = this.#embla.canScrollPrev();
      const canNext = this.#embla.canScrollNext();

      this.toggleAttribute('data-no-overflow', !canPrev && !canNext);
      if (this.prevBtn) this.prevBtn.disabled = !canPrev;
      if (this.nextBtn) this.nextBtn.disabled = !canNext;
    }
  }

  customElements.define('oilogic-carousel', OilogicCarousel);
})();
